import { getAnalytics } from "@tm/insurance/analytics";
import { trackComponentView, trackEvent } from "@tm/insurance/analytics/xdm";
import {
  getFormFieldErrorMsg,
  getTravDetailsFromMemory,
  setTravDetailsInMemory,
} from "@tm/insurance/utils";
import { GENDER_VALUES, TextNodeType } from "@tm/isomorphic-utils";
import { usePlanType } from "@tm/ui/contexts";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FieldArrayWithId,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import TravellerDetailsV2 from "../AddTravellerDetail/TravellerDetailsV2";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import { ImageComponent } from "../ImageComponent/ImageComponent";
import {
  AddButtonWrap,
  AddTravContainer,
  AddTravHeader,
  AddViewButton,
  ArrowIcon,
  Separator,
  ViewButtonWrap,
} from "./AddTravellerDetailsComp.styles";

export interface IGender {
  id: string;
  label: string;
  isSelected: boolean;
}

interface ISecondaryContactV2 {
  name: string;
  dob: string;
  gender: string;
  isSelected: boolean;
  isEdited?: boolean;
  isSavedTraveller?: boolean;
  passport?: string;
  nomineeName?: string;
}

interface TravellerError {
  index: number;
  errorType: string;
}

type FormFieldType = "name" | "dob" | "gender" | "passport" | "nomineeName";

export interface IPrimaryContact {
  email: string;
  mobile: string;
}

type ITravellerData = {
  travellerData: ISecondaryContactV2[];
};

export interface ITravellerDetailsFormV2 {
  travellerData: ISecondaryContactV2[];
  primaryContactData: IPrimaryContact;
  tncs: boolean[];
}

export interface IFormFieldType {
  fieldName?: string;
  isEnabled?: boolean;
  isRequired?: boolean;
  regex?: string;
  title?: string;
  value?: string;
  values?: IGender[];
  placeholder?: string;
  errorMessage?: string;
  countryCode?: string;
}

type AddTravellerDetailsCompProps = {
  title: string;
  subTitle: string;
  savedTravellers?: {
    name: string;
    gender?: string;
    dob?: number;
    passport?: string;
    nomineeName?: string;
  }[];
  secondaryContact: {
    title: string;
    isEditable?: boolean;
    fields: {
      name?: IFormFieldType;
      dob?: IFormFieldType;
      gender?: IFormFieldType;
      passport?: IFormFieldType;
      nomineeName?: IFormFieldType;
    };
  };
  addTravellerCTA: TextNodeType;
  isPassportMandatory: boolean;
  travellerCount?: number;
  formValues: any;
  setValue: any;
  firstErrorIndex?: number;
  setFirstErrorIndex?: (index: number) => void;
  isLoading?: boolean;
  onPayment?: () => void;
  primaryContactRef?: React.RefObject<HTMLDivElement>;
  tncRef?: React.RefObject<HTMLDivElement>;
  isDomesticBooking?: boolean;
  expandedTravellerIndex?: number;
  setExpandedTravellerIndex?: (index: number) => void;
  setSecondaryContactFields: (fields: any) => void;
  visibleTravellersCount: number;
};

const AddTravellerDetailsComp = (data: AddTravellerDetailsCompProps) => {
  const [localAddTrav, setLocalAddTrav] = useState(-1);
  const travellerIndex = 0;
  const {
    formValues,
    title,
    subTitle,
    savedTravellers,
    secondaryContact,
    addTravellerCTA,
    isPassportMandatory,
    travellerCount = 1,
    setValue,
    firstErrorIndex,
    setFirstErrorIndex,
    isLoading = false,
    onPayment,
    primaryContactRef,
    tncRef,
    isDomesticBooking = false,
    expandedTravellerIndex,
    setExpandedTravellerIndex,
    setSecondaryContactFields,
    visibleTravellersCount,
  } = data;

  const scrollToTravellerWithOffset = (index: number) => {
    const element = travellerRefMap.current?.[index];
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const offsetTop = elementRect.top + window.pageYOffset - 100;

      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };
  // Use external expanded state if provided, otherwise use local state
  const addTrav =
    expandedTravellerIndex !== undefined
      ? expandedTravellerIndex
      : localAddTrav;
  const setAddTrav = setExpandedTravellerIndex || setLocalAddTrav;

  // Sync local state with external state when external state changes
  useEffect(() => {
    if (
      expandedTravellerIndex !== undefined &&
      expandedTravellerIndex !== localAddTrav
    ) {
      setLocalAddTrav(expandedTravellerIndex);
    }
  }, [expandedTravellerIndex, localAddTrav]);

  const analytics = getAnalytics();

  const [showAllTravellers, setShowAllTravellers] = useState<boolean>(false);
  const [isSelectedError, setIsSelectedError] = useState<boolean>(false);
  const [isFieldError, setIsFieldError] = useState<boolean>(false);

  const [travellerDataFromStore, setTravellerDataFromStore] = useState<any>({});
  const { travellerDetails } = travellerDataFromStore || {};

  // Read session storage only after client-side hydration
  useEffect(() => {
    const dataFromStorage = getTravDetailsFromMemory();
    setTravellerDataFromStore(dataFromStorage);
  }, []);

  // Handle firstErrorIndex - scroll to error traveller when index changes
  useEffect(() => {
    if (firstErrorIndex && firstErrorIndex > -1) {
      travellerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      setAddTrav(-1);
    }
    if (setFirstErrorIndex) {
      setFirstErrorIndex(-1);
    }
  }, [firstErrorIndex, setFirstErrorIndex]);

  useEffect(() => {
    let travellerDetail: any = [];
    const queryParams = new URLSearchParams(window.location.search);

    setSecondaryContactFields(secondaryContact?.fields);
    if (
      !travellerDetails?.length ||
      queryParams.get("isUpcomingTrip") === "true"
    ) {
      savedTravellers?.map((data: any) => {
        travellerDetail.push({
          ...data,
          gender:
            data.gender.charAt(0).toUpperCase() +
            data.gender.slice(1).toLowerCase(),
          isSelected: false,
          isSavedTraveller: true,
          isEdited: false,
        });
      });
      setTravDetailsInMemory({
        ...travellerDataFromStore,
        travellerDetails: travellerDetail,
      });
      if (travellerDetail.length === 0) {
        setAddTrav(0);
      }
    } else if (travellerDetails && travellerDetails.length > 0) {
      // Use existing session storage data when available
      // This preserves user's exact form data including names, DOB, etc.
      travellerDetail = [...travellerDetails];
    } else {
      // Fallback to create new traveller entries if session storage is empty/undefined
      const queryParams = new URLSearchParams(window.location.search);
      const travellers: number = Number(queryParams.get("travellers"));
      const travellercount = travellerCount || travellers || 1;
      travellerDetail = Array.from({ length: travellercount }).map(() => ({
        name: "",
        dob: "",
        gender: "",
        isSelected: true,
        isEdited: true,
        isSavedTraveller: false,
        passport: "",
        nomineeName: "",
      }));
    }
    let length = travellerDetail?.length ? travellerDetail?.length : 1;

    const formData = Array.from({
      length: length,
    }).map((_, index) => ({
      name: travellerDetail?.[index]?.name || "",
      dob: travellerDetail?.[index]?.isSavedTraveller
        ? travellerDetail?.[index]?.dob || ""
        : travellerDetail?.[index]?.dob || "yyyy-mm-dd",
      gender: travellerDetail?.[index]?.gender || "",
      passport: travellerDetail?.[index]?.passport || "",
      nomineeName: travellerDetail?.[index]?.nomineeName || "",
      isSelected: travellerDetail?.[index]?.isSelected ?? true,
      isEdited: travellerDetail?.[index]?.isEdited ?? true,
      isSavedTraveller: travellerDetail?.[index]?.isSavedTraveller ?? false,
    }));

    setValue("travellerData", formData);
  }, [secondaryContact, savedTravellers, travellerDataFromStore]);

  const {
    control,
    formState: { errors },
    getValues,
    trigger,
    getFieldState,
    setError,
    handleSubmit,
  } = useFormContext<ITravellerDetailsFormV2>();
  const { type } = usePlanType();

  const {
    fields: travellers,
    prepend,
    replace,
  } = useFieldArray({
    control,
    name: "travellerData",
  });

  const handleErrorToast = () => {
    setTimeout(() => {
      setIsSelectedError(false);
      setIsFieldError(false);
    }, 3000);
  };

  const handleGenderChange = (data: IGender, index: number) => {
    setValue(`travellerData.${index}.gender`, data.label);
  };

  const saveFormValuesOnBlur = (index: number = -1) => {
    if (index != -1 && !getValues(`travellerData.${index}.isEdited`)) {
      setValue(`travellerData.${index}.isEdited`, true);
    }
    const { travellerData, primaryContactData } = formValues;
    setTravDetailsInMemory({
      ...travellerDataFromStore,
      travellerDetails: travellerData,
      primaryContactDetails: primaryContactData,
    });
  };

  const handleArrowButton = (index: number) => {
    if (addTrav === index) {
      setAddTrav(-1);
      if (travellerData?.[index].isSelected) {
        trigger(`travellerData.${index}`);
      }
    } else {
      setAddTrav(index);
    }
  };

  const error = errors?.travellerData?.[travellerIndex];
  const travellerRefMap = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const travellerData = useWatch({
    control,
    name: "travellerData",
  });

  const selectedCount = useMemo(
    () =>
      travellerData?.filter((traveller) => traveller.isSelected)?.length ?? 0,
    [travellerData]
  );

  const getVisibleTravellers = () => {
    return showAllTravellers
      ? travellers
      : travellers.slice(0, visibleTravellersCount);
  };
  const travellerRef = useRef<HTMLInputElement>(null);

  const filterTravellers = async (
    selected: number[],
    errors: TravellerError[]
  ) => {
    let targetPosition = 0;
    const newIndices: any = new Array(travellers.length);
    const errorIndices = errors.map((err) => err.index);

    travellers.forEach((traveller, index) => {
      if (errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    travellers.forEach((traveller, index) => {
      if (selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    travellers.forEach((traveller, index) => {
      if (!selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    const newArray: any = [];
    newIndices.forEach((indexVal: number, index: number) => {
      newArray[indexVal] = getValues(`travellerData.${index}`);
    });
    replace(newArray);
    const isValid = await trigger("travellerData");

    errors.forEach(({ index, errorType }, ind) => {
      const fieldKey = `travellerData.${ind}.${errorType}` as
        | `travellerData.${number}.name`
        | `travellerData.${number}.dob`
        | `travellerData.${number}.gender`
        | `travellerData.${number}.passport`
        | `travellerData.${number}.nomineeName`;

      setError(fieldKey, {
        message: getFormFieldErrorMsg(errorType as FormFieldType),
      });
    });
  };

  const handleTravellerSelect = (value: boolean, index: number) => {
    if (getValues(`travellerData.${index}.isSelected`)) {
      setAddTrav(-1);
      setValue(`travellerData.${index}.isSelected`, false);
    } else {
      if (selectedCount === travellerCount) {
        if (!isSelectedError) {
          setIsSelectedError(true);
          handleErrorToast();
        }
      } else {
        setAddTrav(index);
        setValue(`travellerData.${index}.isSelected`, true);
      }
    }
  };

  const handleAddTraveller = () => {
    analytics?.trackTravDetailsEvents({
      actionType: "Add_another_traveller_clicked",
    });
    if (selectedCount != travellerCount) {
      setAddTrav(0);
      prepend({
        name: "",
        dob: "yyyy-mm-dd",
        gender: "",
        passport: "",
        nomineeName: "",
        isSelected: true,
      });
      setTimeout(() => {
        scrollToTravellerWithOffset(0);
      }, 100);
    }
  };

  const handleViewTraveller = () => {
    analytics?.trackTravDetailsEvents({
      actionType: showAllTravellers
        ? "viewLessButton_clicked"
        : "viewAllButton_clicked",
    });
    setShowAllTravellers(!showAllTravellers);
  };

  // Simplified onSubmit handler (since user is always logged in)
  const onSubmit: SubmitHandler<ITravellerDetailsFormV2> = async (formData) => {
    // Track analytics events
    analytics?.trackTravDetailsEvents(
      { actionType: "traveller_name_filled" },
      "pageView",
      type
    );
    analytics?.trackTravDetailsEvents(
      { actionType: "contact_details_filled" },
      "pageView",
      type
    );
    analytics?.trackTravDetailsEvents(
      { actionType: "dob_selected" },
      "pageView",
      type
    );
    analytics?.trackTravDetailsEvents(
      { actionType: "gender_selected" },
      "pageView",
      type
    );

    trackComponentView({
      name: "traveller_name_filled",
      type: "submit",
    });
    trackComponentView({
      name: "contact_details_filled",
      type: "submit",
    });
    trackComponentView({
      name: "dob_selected",
      type: "submit",
    });
    trackComponentView({
      name: "gender_selected",
      type: "submit",
    });

    // Call parent payment handler
    if (onPayment) {
      onPayment();
    }
  };

  // Handle form validation errors with scrolling
  const onSubmitError: SubmitErrorHandler<ITravellerDetailsFormV2> = (
    errors
  ) => {
    if (!isFieldError) {
      setIsFieldError(true);
      handleErrorToast();
    }
    if (errors.travellerData?.length) {
      const errorIndex = errors.travellerData?.findIndex?.((error) => error);
      if (errorIndex != null && errorIndex > -1 && setFirstErrorIndex) {
        setFirstErrorIndex(errorIndex);
      }
    } else if (
      errors.primaryContactData?.email ||
      errors.primaryContactData?.mobile
    ) {
      primaryContactRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "center",
      });
    } else if (errors.tncs?.length) {
      tncRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
    }
  };

  // Main validation and buy handler - similar to handleBuy in AddTravellerDetails
  const handleBuy = () => {
    if (selectedCount !== travellerCount) {
      if (!isFieldError) {
        setIsFieldError(true);
        if (setFirstErrorIndex) {
          setFirstErrorIndex(0);
        }
        handleErrorToast();
      }
    } else {
      if (isDomesticBooking) {
        analytics?.trackTravDetailsEvents(
          { actionType: "buy_clicked_details_domestic" },
          "pageView",
          type
        );
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "buy_clicked_details_domestic",
          },
        });
      } else {
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "buy_clicked",
          },
        });
        analytics?.trackTravDetailsEvents(
          { actionType: "buy_clicked" },
          "pageView",
          type
        );
      }

      const formValues = getValues();
      const selected: number[] = [];
      const errorArr: TravellerError[] = [];
      const { name, dob, gender, passport, nomineeName } =
        secondaryContact?.fields || {};

      formValues?.travellerData?.map((item, index) => {
        const {
          name: fullName,
          dob: dobValue,
          gender: genderType,
          passport: passportType,
          nomineeName: nomineeNameType,
          isSelected,
        } = item || {};

        if (isSelected) {
          selected.push(index);
        }
        const fieldState = getFieldState(`travellerData.${index}`);
        const { isTouched } = fieldState;

        if (!isTouched) {
          if (name && !fullName) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "name",
              });
            }
          } else if (dob && !dobValue) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "dob",
              });
            }
          } else if (gender && !genderType) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "gender",
              });
            }
          } else if (passport && !passportType) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "passport",
              });
            }
          } else if (nomineeName && !nomineeNameType) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "nomineeName",
              });
            }
          }
        }
      });
      filterTravellers(selected, errorArr);
      handleSubmit(onSubmit, onSubmitError)();
    }
  };

  return (
    <AddTravContainer>
      <AddTravHeader>
        <FlexComp flexDirection="column" gap="8px">
          <FormattedText
            value={title}
            baseStyle={{
              fontSize: "22px",
              fontWeight: "900",
              color: "#000000",
            }}
          />
          <FormattedText
            value={subTitle}
            baseStyle={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#4a4a4a",
            }}
          />
        </FlexComp>
        {travellerCount && travellerCount > 0 ? (
          <FormattedText
            value={
              selectedCount?.toString() +
              "/" +
              travellerCount?.toString() +
              " Added"
            }
            baseStyle={{
              color: "#757575",
              fontSize: "14px",
              fontWeight: "700",
            }}
          />
        ) : null}
      </AddTravHeader>
      {getVisibleTravellers().map(
        (
          traveller: FieldArrayWithId<
            ITravellerDetailsFormV2,
            "travellerData",
            "id"
          >,
          index: number
        ) => {
          const { gender, isSelected } =
            formValues?.travellerData?.[index] || {};
          return (
            <div
              key={traveller.id}
              ref={(el) => (travellerRefMap.current[index] = el)}
            >
              <TravellerDetailsV2
                type={type}
                handleTravellerSelect={handleTravellerSelect}
                travellerIndex={index}
                travellerRef={travellerRef}
                isLoading={isLoading}
                firstErrorIndex={firstErrorIndex ?? -1}
                travellerSelected={addTrav == index}
                isSelected={isSelected}
                count={selectedCount}
                travellerNumber={`Traveller ${index + 1}`}
                isSavedTravellers={savedTravellers?.length ? true : false}
                secondaryContactData={secondaryContact?.fields}
                genderTypes={
                  secondaryContact?.fields?.gender?.values || GENDER_VALUES
                }
                selectedGender={gender}
                handleGenderChange={(item) => handleGenderChange(item, index)}
                handleArrowButton={() => handleArrowButton(index)}
                saveFormValuesOnBlur={saveFormValuesOnBlur}
                //   isDomesticBooking={isDomesticBooking}
                // isValidForm={isValidForm}
                isEditable={
                  secondaryContact?.isEditable === false ? false : true
                } //
              />
            </div>
          );
        }
      )}
      <Separator />
      <AddViewButton>
        <AddButtonWrap
          isDisabled={selectedCount === travellerCount}
          onClick={handleAddTraveller}
        >
          <ImageComponent
            width="16px"
            height="16px"
            margin="0px 10px 0px 0px"
            src={
              selectedCount === travellerCount
                ? "https://tripmoneycmsimgak.mmtcdn.com/img/Vector_529ac663fb.svg"
                : "https://tripmoneycmsimgak.mmtcdn.com/img/Vector_68e1489f3a.svg"
            }
          />

          <FormattedText
            value={"ADD ANOTHER TRAVELLER"}
            baseStyle={{
              fontWeight: "800",
            }}
          />
        </AddButtonWrap>

        {travellers.length > visibleTravellersCount && (
          <ViewButtonWrap onClick={handleViewTraveller}>
            <FormattedText
              value={showAllTravellers ? "VIEW LESS" : "VIEW ALL"}
              baseStyle={{
                fontWeight: "800",
              }}
            />

            <ArrowIcon
              isRotated={showAllTravellers}
              src="https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
              alt="View toggle icon"
            />
          </ViewButtonWrap>
        )}
      </AddViewButton>
    </AddTravContainer>
  );
};

export default AddTravellerDetailsComp;
